import { format } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Button, Icon, IconButton, Panel, Tooltip, Typography } from "@/components/atoms";
import { useLazyRequestVehicleDocumentQuery } from "@/redux/apis/document/documentApi";
import { useGetVehicleFormOptionsQuery } from "@/redux/apis/vehicle/vehicleApi";
import { VehicleDocument } from "@/redux/slices/vehicle/types";
import { clsx, downloadFile, getAssetUrl, getCountryFlagSrc } from "@/utils";
import { getAccreditationStatus, getInsuranceStatus, getRegistrationStatus } from "../MyVehicles/helpers";
import { useProfile } from "@/hooks";
import { useEditVehicleContext } from "./context";
import { EditVehicleFormData } from "./form";
import { ChangePlateNumberModal } from "./modals/ChangePlateNumberModal";
import { ChangeVehicleStateModal } from "./modals/ChangeVehicleStateModal";
import { CompleteAllRequiredFieldsModal } from "./modals/CompleteAllRequiredFieldsModal";
import { FirstTimeVerificationModal } from "./modals/FirstTimeVerificationModal";
import { UpdateVerificationModal } from "./modals/UpdateVerificationModal";
import { UploadDocumentModal } from "./modals/UploadDocumentModal";
import { WarningAffectVerificationModal } from "./modals/WarningAffectVerificationModal";

type ModalStep =
  | "verify"
  | "update-verification"
  | "complete-required-fields"
  | "upload-insurance"
  | "upload-accreditation"
  | "warning-plate"
  | "warning-state"
  | "change-plate"
  | "change-state"
  | undefined;

export const Sidebar = () => {
  const { vehicle } = useEditVehicleContext();
  const { dateFormat } = useProfile();
  const { watch } = useFormContext<EditVehicleFormData>();
  useGetVehicleFormOptionsQuery(); // Pre-fetch options for child modals
  const [step, setStep] = useState<ModalStep>();

  const registrationStatus = getRegistrationStatus(vehicle);
  const insuranceStatus = getInsuranceStatus(vehicle);
  const accreditationStatus = getAccreditationStatus(vehicle);

  const overallStatus = useMemo(() => {
    if (registrationStatus === "missing-details" || insuranceStatus === "missing-details") return "missing-details";
    if (registrationStatus === "unverified" || insuranceStatus === "unverified") return "unverified";
    if (registrationStatus === "expired" || insuranceStatus === "expired" || accreditationStatus === "expired") return "expired";
    if (registrationStatus === "expiring" || insuranceStatus === "expiring" || accreditationStatus === "expiring") return "expiring";
    return registrationStatus;
  }, [registrationStatus, insuranceStatus, accreditationStatus]);

  const isVerified = (overallStatus === "verified" || overallStatus === "expiring") && !!vehicle.registration.lastUpdated;

  const hasVehicleType = !!watch("vehicleTypeUuid");
  const insurance = vehicle.documents.find((doc) => doc.documentType === "INSURANCE");
  const isCompleteRequirements = Boolean(hasVehicleType && insurance);

  const handleVerify = () => {
    if (isCompleteRequirements) {
      if (isVerified) setStep("update-verification");
      else setStep("verify");
    } else {
      setStep("complete-required-fields");
    }
  };

  const insuranceDoc = vehicle.documents.find((d) => d.documentType === "INSURANCE");
  const accreditationDoc = vehicle.documents.find((d) => d.documentType === "ACCREDITATION");

  const flagSrc = getCountryFlagSrc(vehicle.country.countryCode);

  return (
    <>
      <Panel className="w-full shrink-0 xl:w-[380px]">
        {/* Header */}
        <Typography variant="small" className="text-xs uppercase tracking-wider text-neutral-silver-gray">
          Vehicle Preview
        </Typography>
        <Typography variant="h2" className="mt-2">
          Vehicle Registration
        </Typography>
        <Typography variant="paragraph" className="text-neutral-dark-gray">
          Ensure all required documents are valid and up to date for this vehicle to remain verified
        </Typography>

        {/* Vehicle Info */}
        <div className="mt-4 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full border border-neutral-mid-gray">
            <img
              src={getAssetUrl(vehicle.model.avatar, { height: 56, width: 56, background: "transparent", fit: "contain" })}
              className="h-7 w-7"
              alt={`${vehicle.model.make} ${vehicle.model.model}`}
            />
          </div>
          <div className="flex flex-col">
            <Typography variant="title">
              {vehicle.model.make} {vehicle.model.model}
            </Typography>
            <div className="flex items-center gap-1">
              <Typography variant="paragraph" className="text-neutral-dark-gray">
                {vehicle.registrationNumber}
              </Typography>
              <IconButton
                iconName="Edit2"
                variant="custom"
                iconSize="sm"
                className="text-neutral-dark-gray"
                onClick={() => setStep("warning-plate")}
              />
            </div>
          </div>
        </div>

        {/* Vehicle Details */}
        <div className="mt-4 flex flex-col gap-2">
          <DetailRow label="Year" value={vehicle.year?.toString() ?? "-"} />
          <DetailRow
            label="Color"
            value={
              vehicle.color ? (
                <span className="inline-flex items-center gap-1.5 capitalize">
                  <span
                    className="inline-block h-3 w-3 rounded-full border border-black"
                    style={{ backgroundColor: vehicle.color.toLowerCase().replace("_", "") }}
                  />
                  {vehicle.color.toLowerCase().replace("_", " ")}
                </span>
              ) : (
                "-"
              )
            }
          />
          <DetailRow label="Engine Type" value={vehicle.model.engine ? vehicle.model.engine.toLowerCase() : "-"} />
          <DetailRow
            label="State Registered"
            value={
              <span className="inline-flex items-center gap-1">
                {flagSrc && <img src={flagSrc} className="h-[18px] w-[18px] rounded-full object-cover" alt="" />}
                {vehicle.state.abbreviation}
                <IconButton
                  iconName="Edit2"
                  variant="custom"
                  iconSize="sm"
                  className="text-neutral-dark-gray"
                  onClick={() => setStep("warning-state")}
                />
              </span>
            }
          />
          <DetailRow label="VIN" value={vehicle.vin ?? "-"} />
          <DetailRow
            label={
              <span className="inline-flex items-center gap-1">
                Vehicle Category
                <Tooltip content="System-defined category based on vehicle make/model" placement="top">
                  <Icon name="InfoCircle" variant="Bold" className="rotate-180 text-neutral-silver-gray" size="sm" />
                </Tooltip>
              </span>
            }
            value={vehicle.vehicleCategory?.name ?? "-"}
          />
        </div>

        {/* Verification Status Banner */}
        <VerificationBanner
          status={overallStatus}
          isVerified={isVerified}
          lastUpdated={vehicle.registration.lastUpdated}
          dateFormat={dateFormat}
        />

        {/* Document Rows */}
        <div className="mt-4 space-y-3 rounded-lg bg-neutral-surface-gray p-3">
          <RegistrationRow
            status={registrationStatus}
            expiryDate={vehicle.registration.expiryDate}
            dateFormat={dateFormat}
            onReconfirm={handleVerify}
          />
          <DocumentRow
            label="Insurance"
            document={insuranceDoc}
            status={insuranceStatus}
            vehicleId={vehicle.uuid}
            dateFormat={dateFormat}
            onUpload={() => setStep("upload-insurance")}
          />
          <DocumentRow
            label="Accreditation"
            document={accreditationDoc}
            status={accreditationStatus}
            vehicleId={vehicle.uuid}
            dateFormat={dateFormat}
            onUpload={() => setStep("upload-accreditation")}
          />
        </div>

        {/* Footer Warning */}
        <Typography variant="paragraph" className="mt-5 text-center text-neutral-silver-gray">
          A vehicle cannot do any work in the system if the vehicle is unverified
        </Typography>
      </Panel>

      {/* Modals */}
      <FirstTimeVerificationModal vehicle={vehicle} open={step === "verify"} onClose={() => setStep(undefined)} />
      <UpdateVerificationModal vehicle={vehicle} open={step === "update-verification"} onClose={() => setStep(undefined)} />
      <CompleteAllRequiredFieldsModal open={step === "complete-required-fields"} onClose={() => setStep(undefined)} />
      <UploadDocumentModal
        vehicleId={vehicle.uuid}
        documentType="INSURANCE"
        open={step === "upload-insurance"}
        onClose={() => setStep(undefined)}
      />
      <UploadDocumentModal
        vehicleId={vehicle.uuid}
        documentType="ACCREDITATION"
        open={step === "upload-accreditation"}
        onClose={() => setStep(undefined)}
      />
      <WarningAffectVerificationModal
        open={step === "warning-plate" || step === "warning-state"}
        onClose={() => setStep(undefined)}
        onContinue={() => setStep(step === "warning-plate" ? "change-plate" : "change-state")}
      />
      <ChangePlateNumberModal vehicle={vehicle} open={step === "change-plate"} onClose={() => setStep(undefined)} />
      <ChangeVehicleStateModal vehicle={vehicle} open={step === "change-state"} onClose={() => setStep(undefined)} />
    </>
  );
};

const DetailRow = ({ label, value }: { label: React.ReactNode; value: React.ReactNode }) => (
  <div className="flex items-center justify-between">
    <Typography variant="action" className="text-neutral-dark-gray">{label}</Typography>
    <Typography variant="paragraph" className="capitalize text-neutral-black">{value}</Typography>
  </div>
);

const VerificationBanner = ({
  status,
  isVerified,
  lastUpdated,
  dateFormat,
}: {
  status: string;
  isVerified: boolean;
  lastUpdated: string | null;
  dateFormat: string;
}) => {
  if (isVerified && (status === "verified" || status === "expiring")) {
    return (
      <div className={clsx("mt-4 rounded-lg p-4", status === "expiring" ? "bg-warning" : "bg-info")}>
        <div className="flex items-center gap-2">
          <Icon name="Verify" variant="Bold" size="sm" className="text-white" />
          <Typography variant="title" className="text-white">
            RideMinder Verified
          </Typography>
        </div>
        <Typography variant="paragraph" className="mt-1 text-white">
          {status === "expiring"
            ? "Some documents are nearing expiry. Renew early to maintain continuous verification."
            : `Verified by RideMinder last ${lastUpdated ? format(new Date(lastUpdated), dateFormat) : "-"}`}
        </Typography>
      </div>
    );
  }

  if (status === "expired" || status === "unverified" || status === "missing-details" || status === "unknown") {
    return (
      <div className="mt-4 rounded-lg bg-neutral-gray p-4">
        <div className="flex items-center gap-2">
          <Icon name="Verify" variant="Bold" size="sm" className="text-neutral-silver-gray" />
          <Typography variant="title" className="text-neutral-dark-gray">
            Verification Paused
          </Typography>
        </div>
        <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
          The verification has been paused due to an expired document.
        </Typography>
      </div>
    );
  }

  return null;
};

const StatusIcon = ({ status }: { status: string }) => {
  switch (status) {
    case "verified":
      return <Icon name="TickCircle" variant="Bold" size="sm" className="shrink-0 text-success" />;
    case "expiring":
      return <Icon name="Danger" variant="Bold" size="sm" className="shrink-0 text-warning" />;
    case "expired":
    case "missing-details":
    case "unverified":
    case "unknown":
      return <Icon name="InfoCircle" variant="Bold" size="sm" className="shrink-0 text-danger" />;
    default:
      return <Icon name="TickCircle" variant="Bold" size="sm" className="shrink-0 text-neutral-dark-gray" />;
  }
};

const RegistrationRow = ({
  status,
  expiryDate,
  dateFormat,
  onReconfirm,
}: {
  status: string;
  expiryDate: string | null;
  dateFormat: string;
  onReconfirm: () => void;
}) => {
  const isActive = status === "verified";

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <StatusIcon status={status} />
        <div>
          <Typography variant="action" className="flex items-center gap-1 text-sm">
            Registration
            <Tooltip
              content="This is the vehicle's official registration record. Once it's renewed, you can re-register the vehicle. The system will automatically detect the update, so no document upload is needed."
              placement="top"
            >
              <Icon name="InfoCircle" size="sm" className="rotate-180 text-neutral-dark-gray" />
            </Tooltip>
          </Typography>
          <Typography variant="paragraph" className="text-neutral-dark-gray">
            {expiryDate ? `Expiry: ${format(new Date(expiryDate), dateFormat)}` : "No expiry date"}
          </Typography>
        </div>
      </div>
      {isActive ? (
        <Tooltip
          content="The vehicle's registration is still active and hasn't reached its renewal period."
          placement="top"
        >
          <span className="shrink-0">
            <Button variant="primary" size="sm" className="pointer-events-none text-xs opacity-50">
              Reconfirm Registration
            </Button>
          </span>
        </Tooltip>
      ) : (
        <Button variant="primary" size="sm" onClick={onReconfirm} className="shrink-0 text-xs">
          Reconfirm Registration
        </Button>
      )}
    </div>
  );
};

const DocumentRow = ({
  label,
  document,
  status,
  vehicleId,
  dateFormat,
  onUpload,
}: {
  label: string;
  document?: VehicleDocument;
  status: string;
  vehicleId: string;
  dateFormat: string;
  onUpload: () => void;
}) => {
  const [requestDocument] = useLazyRequestVehicleDocumentQuery();

  const handleDownload = useCallback(() => {
    if (!document) return;
    requestDocument({ id: document.uuid, fileType: "pdf", vehicleId })
      .unwrap()
      .then((res) => {
        downloadFile(res.filename, document.uuid);
      });
  }, [document, requestDocument, vehicleId]);

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <StatusIcon status={status} />
        <div>
          <Typography variant="action" className="text-sm">
            {label}
          </Typography>
          <Typography variant="paragraph" className="text-neutral-dark-gray">
            {document?.expiryDate ? `Expiry: ${format(new Date(document.expiryDate), dateFormat)}` : "No document"}
          </Typography>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {document && (
          <Tooltip content="Download Document" placement="top">
            <IconButton iconName="ImportCurve" variant="custom" iconSize="md" className="text-neutral-dark-gray" onClick={handleDownload} />
          </Tooltip>
        )}
        <Tooltip content="Upload New Document" placement="top">
          <IconButton
            iconName="ExportCurve"
            variant="custom"
            iconSize="md"
            className="text-neutral-dark-gray"
            onClick={onUpload}
          />
        </Tooltip>
      </div>
    </div>
  );
};
